<script lang="ts" setup>
  import { ref, nextTick } from 'vue';
  import { useBreakpoints } from '@storefront/storefront';
  import type { Pagination } from '@storefront/types';
  import { StoreLayout, AccountLayout } from '@storefront/components';
  import type { ProductForReview } from '@storefront/resources';


  defineOptions({
    layout: [StoreLayout, AccountLayout],
    inheritAttrs: false,
  });

  interface Props {
    available: Pagination<ProductForReview>;
  }

  defineProps<Props>();

  const breakpoints = useBreakpoints();

  const product = ref<ProductForReview | null>(null);
  const open = ref(false);

  function sendReview(item: ProductForReview) {
    product.value = item;
    nextTick(() => open.value = true);
  }
</script>
<template>
  <AccountMobileNavigation :href="$route('web.account.show')">
    {{ $t('SHOP.ACCOUNT.HEADER.REVIEW_SEND') }}
  </AccountMobileNavigation>
  <SfTile class="account-review" large>
    <template v-if="breakpoints.lg" #title>
      <h1 class="account__heading">
        {{ $t('SHOP.ACCOUNT.HEADER.REVIEW_SEND') }}
      </h1>
    </template>
    <template #default>
      <div v-if="available.data.length > 0" class="account-review__products">
        <SfListCard v-for="item in available.data">
          <template #image>
            <MediaPicture :photo="item.photo" class="account-review__product-image" sizes="140x140 2x, 70x70" />
          </template>
          <template #default>
            <div class="account-review__product-content">
              <h2 class="account-review__product-title">
                {{ item.title }}
              </h2>
            </div>
          </template>
          <template #actions>
            <SfButton @click="sendReview(item)">
              {{ $t('SHOP.ACCOUNT.ACTION.REVIEW_SEND') }}
            </SfButton>
          </template>
        </SfListCard>
      </div>
      <SfDataPlaceholder v-else>
        {{ $t('SHOP.ACCOUNT.MESSAGE.REVIEW_NO_PRODUCTS') }}
      </SfDataPlaceholder>
    </template>
  </SfTile>

  <SfPagination :pagination="available" />
  <ReviewFormModal v-if="product" v-model:open="open" :variant="product" />

  <RouterView />
</template>
<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  .account-review {
    &__products,
    &__product-content,
    &__form-product,
    &__form-content {
      display: flex;
      flex-direction: column;
    }

    &__products {
      row-gap: 1rem;

      :deep(.list-card__image) {
        flex: 0 0 70px;
      }
    }

    &__product-content,
    &__form-product {
      gap: 0.5rem;
    }

    &__product-title {
      font-size: 1.125rem;
    }

    &__product-sku {
      font-size: 0.875rem;
      color: var(--sf-account-color-dark-grey);
    }

    &__form-main {
      flex-grow: 1;
    }

    .list-card__image {
      flex: 0 0 80px;
    }

    &__product-title--form {
      font-size: 1.125rem;
    }

    &__product-sku {
      font-size: 0.875rem;
      color: var(--sf-account-color-dark-grey);
    }
  }


  @include media.query(lg) {
    .account-review {
      &__products .card {
        padding-bottom: 1rem;
      }

      &__content {
        flex-direction: column;
        gap: 0.75rem;
      }

      &__form .card {
        border: none;
      }

      &__product-title {
        font-size: 1rem;
      }
    }
  }
</style>
